/**
 * AI prompts view — Ticketing Configuration › AI Prompts.
 *
 * Renders one card per prompt code with JSON editors for the prompt
 * `body`, `settings`, and `meta` (the shape the backend stores and the AI
 * pipeline consumes), a version-history dialog with rollback, and a
 * save-back handler that POSTs a new version.
 *
 * Backend contract (see inet_portal/.../ticketing/admin_ai_prompts.py):
 *   GET  /ai-prompts                       → { items: [ { code, description,
 *           meta, current_version: { version, body, settings, diff_notes } } ] }
 *   POST /ai-prompts/{code}/versions       ← { body, settings, meta,
 *                                              description, diff_notes }
 *   GET  /ai-prompts/{code}/versions       → { items: [ version… ] }
 *   POST /ai-prompts/{code}/versions/{n}/rollback
 *
 * Loaded as a module before admin.js; registers `window.adminViews.aiPrompts`.
 * Channel-settings boot calls `attachListeners()` + `loadAiPrompts()`.
 */

import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- Constants ------------------------------------------------------
  const AI_PROMPT_LABELS = {
    incoming_message: 'Incoming message intake',
    agent_internal_comment: 'Internal comment review',
    agent_external_comment: 'Customer reply review',
    customer_feedback: 'Customer feedback analysis',
    attachment_review: 'Attachment inspection',
    tech_ticket_closure_comment: 'Technician closure quality review',
    customer_retention_score: 'Customer retention scoring',
  };

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let aiPromptsContainer, refreshPromptsBtn, aiPromptHistoryDlg,
      aiPromptHistoryTitle, aiPromptHistoryList, aiPromptHistoryStatus;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    aiPromptsContainer = document.getElementById('aiPromptsContainer');
    refreshPromptsBtn = document.getElementById('refreshPromptsBtn');
    aiPromptHistoryDlg = document.getElementById('aiPromptHistoryDlg');
    aiPromptHistoryTitle = document.getElementById('aiPromptHistoryTitle');
    aiPromptHistoryList = document.getElementById('aiPromptHistoryList');
    aiPromptHistoryStatus = document.getElementById('aiPromptHistoryStatus');
  }

  // ---- State ----------------------------------------------------------
  // The module owns its prompt list. (A getter can be injected via
  // configurePromptsState for callers that want to share state, but it
  // defaults to our own object so the view works standalone.)
  const promptsState = { prompts: [], loadingPrompts: false };
  let getPromptsState = () => promptsState;
  function configurePromptsState(getter) {
    if (typeof getter === 'function') getPromptsState = getter;
  }

  const aiPromptHistoryState = {
    code: null,
    items: [],
    activeVersion: null,
    loading: false,
  };
  // code -> { bodyEditor, settingsEditor, metaEditor, descriptionInput, diffNotesInput }
  const aiPromptEditors = new Map();

  function getPromptLabel(code) {
    return AI_PROMPT_LABELS[code] || String(code || '').replace(/_/g, ' ');
  }

  function destroyAiPromptEditors() {
    aiPromptEditors.forEach((entry) => {
      ['bodyEditor', 'settingsEditor', 'metaEditor'].forEach((key) => {
        const ed = entry && entry[key];
        if (ed && typeof ed.destroy === 'function') {
          try { ed.destroy(); } catch (err) { console.warn('Failed to destroy editor', err); }
        }
      });
    });
    aiPromptEditors.clear();
  }

  // A minimal JSON/text editor over a <textarea> (no external dependency).
  function createCodeEditor(textarea, initialValue) {
    if (!textarea) return null;
    textarea.value = initialValue || '';
    textarea.classList.add('json-editor');
    return {
      getValue: () => textarea.value,
      setValue: (value) => { textarea.value = value || ''; },
      destroy: () => { textarea.classList.remove('json-editor'); },
    };
  }

  function _jsonString(value) {
    try {
      return JSON.stringify(value || {}, null, 2);
    } catch (err) {
      return '{}';
    }
  }

  function _makeJsonField(parent, labelText, code, role, value) {
    const field = document.createElement('div');
    field.style.marginTop = '12px';
    const label = document.createElement('label');
    label.textContent = labelText;
    label.style.display = 'block';
    label.style.fontSize = '12px';
    label.style.color = 'var(--sub)';
    field.appendChild(label);
    const textarea = document.createElement('textarea');
    textarea.rows = 8;
    textarea.style.width = '100%';
    textarea.style.fontFamily = 'ui-monospace, Menlo, Monaco, monospace';
    textarea.dataset.role = role;
    textarea.dataset.code = code;
    field.appendChild(textarea);
    parent.appendChild(field);
    return createCodeEditor(textarea, value);
  }

  function renderAiPrompts() {
    if (!aiPromptsContainer) return;
    destroyAiPromptEditors();
    const state = getPromptsState();
    const prompts = Array.isArray(state.prompts) ? state.prompts : [];
    const loading = !!state.loadingPrompts;
    if (loading && !prompts.length) {
      aiPromptsContainer.innerHTML = '<div class="hint" style="color: var(--sub);">Loading AI prompts…</div>';
      return;
    }
    if (!prompts.length) {
      aiPromptsContainer.innerHTML = '<div class="hint" style="color: var(--sub);">No AI prompts configured.</div>';
      return;
    }
    aiPromptsContainer.innerHTML = '';

    prompts.forEach((prompt) => {
      const code = prompt && prompt.code ? prompt.code : '';
      if (!code) return;
      const currentVersion = prompt.current_version || null;
      const meta = prompt.meta || {};

      const card = document.createElement('div');
      card.className = 'ai-prompt-card field';
      card.dataset.aiPromptCode = code;

      // Header: label + code + active version badge + actions.
      const header = document.createElement('div');
      header.style.display = 'flex';
      header.style.justifyContent = 'space-between';
      header.style.alignItems = 'center';
      header.style.gap = '12px';

      const titleWrap = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = getPromptLabel(code);
      titleWrap.appendChild(title);
      const codeEl = document.createElement('div');
      codeEl.className = 'hint';
      codeEl.style.color = 'var(--sub)';
      codeEl.textContent = currentVersion && currentVersion.version != null
        ? `${code} · active v${currentVersion.version}`
        : `${code} · no active version`;
      titleWrap.appendChild(codeEl);
      header.appendChild(titleWrap);

      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.gap = '8px';
      const historyBtn = document.createElement('button');
      historyBtn.type = 'button';
      historyBtn.className = 'btn ghost';
      historyBtn.textContent = '🕒 History';
      historyBtn.dataset.action = 'history';
      historyBtn.dataset.code = code;
      actions.appendChild(historyBtn);
      const saveBtn = document.createElement('button');
      saveBtn.type = 'button';
      saveBtn.className = 'btn';
      saveBtn.textContent = '💾 Save new version';
      saveBtn.dataset.action = 'save';
      saveBtn.dataset.code = code;
      actions.appendChild(saveBtn);
      header.appendChild(actions);
      card.appendChild(header);

      if (prompt.description) {
        const desc = document.createElement('div');
        desc.className = 'hint';
        desc.style.marginTop = '6px';
        desc.style.color = 'var(--sub)';
        desc.textContent = prompt.description;
        card.appendChild(desc);
      }

      const placeholders = Array.isArray(meta.required_placeholders) ? meta.required_placeholders : [];
      if (placeholders.length) {
        const ph = document.createElement('div');
        ph.className = 'hint';
        ph.style.marginTop = '6px';
        ph.style.color = 'var(--sub)';
        ph.textContent = `Required placeholders: ${placeholders.join(', ')}`;
        card.appendChild(ph);
      }

      // Description (editable) — kept simple as a single-line input.
      const descField = document.createElement('div');
      descField.style.marginTop = '12px';
      const descLabel = document.createElement('label');
      descLabel.textContent = 'Description';
      descLabel.style.display = 'block';
      descLabel.style.fontSize = '12px';
      descLabel.style.color = 'var(--sub)';
      descField.appendChild(descLabel);
      const descriptionInput = document.createElement('input');
      descriptionInput.type = 'text';
      descriptionInput.style.width = '100%';
      descriptionInput.placeholder = 'Optional description';
      descriptionInput.value = prompt.description || '';
      descField.appendChild(descriptionInput);
      card.appendChild(descField);

      // JSON editors for body / settings / meta.
      const bodyEditor = _makeJsonField(card, 'Prompt body (JSON)', code, 'body', _jsonString(currentVersion && currentVersion.body));
      const settingsEditor = _makeJsonField(card, 'Settings (JSON)', code, 'settings', _jsonString(currentVersion && currentVersion.settings));
      const metaEditor = _makeJsonField(card, 'Meta (JSON)', code, 'meta', _jsonString(meta));

      // Diff notes for the audit trail.
      const notesField = document.createElement('div');
      notesField.style.marginTop = '12px';
      const notesLabel = document.createElement('label');
      notesLabel.textContent = 'Change notes (audit trail)';
      notesLabel.style.display = 'block';
      notesLabel.style.fontSize = '12px';
      notesLabel.style.color = 'var(--sub)';
      notesField.appendChild(notesLabel);
      const diffNotesInput = document.createElement('textarea');
      diffNotesInput.rows = 2;
      diffNotesInput.style.width = '100%';
      notesField.appendChild(diffNotesInput);
      card.appendChild(notesField);

      aiPromptsContainer.appendChild(card);

      aiPromptEditors.set(code, {
        bodyEditor,
        settingsEditor,
        metaEditor,
        descriptionInput,
        diffNotesInput,
        prompt,
      });
    });
  }

  function _parseJsonField(rawText, fieldLabel, code, { required = false } = {}) {
    const text = (rawText || '').trim();
    if (!text) {
      if (required) {
        throw new Error(`${fieldLabel} for ${getPromptLabel(code)} cannot be empty`);
      }
      return {};
    }
    try {
      return JSON.parse(text);
    } catch (err) {
      throw new Error(`Invalid ${fieldLabel} JSON for ${getPromptLabel(code)}: ${err.message}`);
    }
  }

  async function saveAiPrompt(code) {
    const entry = aiPromptEditors.get(code);
    if (!entry) return;

    let payload;
    try {
      payload = {
        body: _parseJsonField(entry.bodyEditor ? entry.bodyEditor.getValue() : '', 'prompt body', code, { required: true }),
        settings: _parseJsonField(entry.settingsEditor ? entry.settingsEditor.getValue() : '', 'settings', code),
        meta: _parseJsonField(entry.metaEditor ? entry.metaEditor.getValue() : '', 'meta', code),
        description: entry.descriptionInput && entry.descriptionInput.value.trim()
          ? entry.descriptionInput.value.trim()
          : null,
        diff_notes: entry.diffNotesInput && entry.diffNotesInput.value.trim()
          ? entry.diffNotesInput.value.trim()
          : null,
      };
    } catch (err) {
      showFlash(err.message, 'error');
      return;
    }

    const saveBtn = aiPromptsContainer
      ? aiPromptsContainer.querySelector(`button[data-action="save"][data-code="${code}"]`)
      : null;
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.dataset.originalText = saveBtn.textContent;
      saveBtn.textContent = 'Saving…';
    }
    try {
      await adminAPI.fetch(API.adminAIPromptVersions(code), { method: 'POST', body: JSON.stringify(payload) });
      showFlash(`${getPromptLabel(code)} saved`);
      await loadAiPrompts();
      if (aiPromptHistoryState.code === code && aiPromptHistoryDlg && aiPromptHistoryDlg.open) {
        await loadAiPromptHistory(code).catch(() => {});
      }
    } catch (err) {
      showFlash(err.message || `Failed to save ${getPromptLabel(code)}`, 'error');
    } finally {
      if (saveBtn) {
        saveBtn.disabled = false;
        if (saveBtn.dataset.originalText) {
          saveBtn.textContent = saveBtn.dataset.originalText;
          delete saveBtn.dataset.originalText;
        }
      }
    }
  }

  function handleAiPromptContainerClick(event) {
    const target = event.target.closest('button[data-action]');
    if (!target) return;
    const action = target.dataset.action;
    const code = target.dataset.code;
    if (!code) return;
    if (action === 'save') {
      saveAiPrompt(code);
    } else if (action === 'history') {
      openAiPromptHistory(code);
    }
  }

  // Load a historical version's body/settings into the live editor (meta is
  // prompt-level, not per-version, so it is left untouched).
  function loadAiPromptIntoEditor(code, version) {
    const entry = aiPromptEditors.get(code);
    if (!entry || !version) return;
    if (entry.bodyEditor) entry.bodyEditor.setValue(_jsonString(version.body));
    if (entry.settingsEditor) entry.settingsEditor.setValue(_jsonString(version.settings));
  }

  async function loadAiPrompts() {
    resolveDom();
    const state = getPromptsState();
    if (!aiPromptsContainer) {
      // eslint-disable-next-line no-console
      console.warn('ai-prompts: #aiPromptsContainer missing — prompts will not load.');
      return;
    }
    state.loadingPrompts = true;
    renderAiPrompts();
    try {
      const data = await adminAPI.fetch(API.adminAIPrompts, { method: 'GET' });
      state.prompts = Array.isArray(data && data.items) ? data.items : (Array.isArray(data) ? data : []);
    } catch (err) {
      state.prompts = [];
      showFlash(err.message || 'Failed to load AI prompts', 'error');
    } finally {
      state.loadingPrompts = false;
      renderAiPrompts();
    }
  }

  async function openAiPromptHistory(code) {
    if (!aiPromptHistoryDlg || !aiPromptHistoryList) { console.warn('ai-prompts: history dialog missing.'); return; }
    aiPromptHistoryState.code = code;
    aiPromptHistoryState.items = [];
    // Remember which version is currently active so history can flag it.
    const state = getPromptsState();
    const prompt = (Array.isArray(state.prompts) ? state.prompts : []).find((p) => p && p.code === code);
    aiPromptHistoryState.activeVersion = prompt && prompt.current_version
      ? Number(prompt.current_version.version)
      : null;
    if (aiPromptHistoryTitle) aiPromptHistoryTitle.textContent = `History — ${getPromptLabel(code)}`;
    if (aiPromptHistoryStatus) aiPromptHistoryStatus.textContent = 'Loading…';
    aiPromptHistoryList.innerHTML = '';
    openDialog(aiPromptHistoryDlg);
    await loadAiPromptHistory(code);
  }

  async function loadAiPromptHistory(code) {
    if (!aiPromptHistoryList) { console.warn('ai-prompts: #aiPromptHistoryList missing.'); return; }
    aiPromptHistoryState.loading = true;
    if (aiPromptHistoryStatus) aiPromptHistoryStatus.textContent = 'Loading…';
    try {
      const data = await adminAPI.fetch(API.adminAIPromptVersions(code), { method: 'GET' });
      aiPromptHistoryState.items = Array.isArray(data && data.items) ? data.items : [];
      renderAiPromptHistory();
      if (aiPromptHistoryStatus) aiPromptHistoryStatus.textContent = '';
    } catch (err) {
      if (aiPromptHistoryStatus) aiPromptHistoryStatus.textContent = err.message || 'Failed to load history';
    } finally {
      aiPromptHistoryState.loading = false;
    }
  }

  function renderAiPromptHistory() {
    if (!aiPromptHistoryList) return;
    aiPromptHistoryList.innerHTML = '';
    if (!aiPromptHistoryState.items.length) {
      aiPromptHistoryList.innerHTML = '<div class="hint" style="color: var(--sub);">No history.</div>';
      return;
    }
    const code = aiPromptHistoryState.code || '';
    const activeVersion = aiPromptHistoryState.activeVersion;
    aiPromptHistoryState.items.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'ai-prompt-history-item';
      row.style.border = '1px solid var(--border)';
      row.style.borderRadius = '6px';
      row.style.padding = '10px';
      row.style.marginTop = '8px';
      const created = item.created_at ? formatDateTimeFull(item.created_at) : '—';
      const author = item.created_by_user_id != null ? item.created_by_user_id : '—';
      const isActive = activeVersion != null && Number(item.version) === activeVersion;
      const status = isActive ? '<span class="badge ok">active</span>' : '<span class="badge off">inactive</span>';
      const notesHtml = item.diff_notes
        ? `<div class="hint" style="color: var(--sub); margin-top:4px;">${escapeHtml(item.diff_notes)}</div>`
        : '';
      row.innerHTML = `
        <div style="display:flex; justify-content:space-between; gap:12px; align-items:center;">
          <div>
            <strong>v${escapeHtml(String(item.version))}</strong> · ${status}
            <div class="hint" style="color: var(--sub); margin-top:4px;">Created ${escapeHtml(created)} · Author #${escapeHtml(String(author))}</div>
            ${notesHtml}
          </div>
          <div style="display:flex; gap:8px;">
            ${isActive ? '' : `<button class="btn" data-action="rollback" data-code="${escapeHtml(code)}" data-version="${escapeHtml(String(item.version))}">Activate</button>`}
            <button class="btn ghost" data-action="load-version" data-code="${escapeHtml(code)}" data-version="${escapeHtml(String(item.version))}">Load into editor</button>
          </div>
        </div>
        <details style="margin-top:8px;"><summary>Body</summary><pre style="background: var(--surface-alt); padding:8px; border-radius:6px; overflow:auto;">${escapeHtml(_jsonString(item.body))}</pre></details>
        <details style="margin-top:6px;"><summary>Settings</summary><pre style="background: var(--surface-alt); padding:8px; border-radius:6px; overflow:auto;">${escapeHtml(_jsonString(item.settings))}</pre></details>
      `;
      aiPromptHistoryList.appendChild(row);
    });
  }

  function handleAiPromptHistoryClick(event) {
    const btn = event.target.closest('button[data-action]');
    if (!btn) return;
    const action = btn.dataset.action;
    const code = btn.dataset.code;
    const version = btn.dataset.version;
    if (!code || !version) return;
    if (action === 'rollback') {
      rollbackAiPromptVersion(code, Number(version), btn);
    } else if (action === 'load-version') {
      const entry = aiPromptHistoryState.items.find((item) => String(item.version) === String(version));
      if (entry) {
        loadAiPromptIntoEditor(code, entry);
        if (aiPromptHistoryDlg && aiPromptHistoryDlg.open) {
          aiPromptHistoryDlg.close();
        }
        showFlash(`Loaded v${version} into editor`);
      }
    }
  }

  async function rollbackAiPromptVersion(code, version, triggerBtn) {
    if (!code || !version) return;
    if (!confirm(`Activate v${version} for ${getPromptLabel(code)}?`)) return;
    if (triggerBtn) {
      triggerBtn.disabled = true;
      triggerBtn.dataset.originalText = triggerBtn.textContent;
      triggerBtn.textContent = 'Activating…';
    }
    try {
      await adminAPI.fetch(API.adminAIPromptRollback(code, version), { method: 'POST', body: '{}' });
      showFlash(`Activated v${version} for ${getPromptLabel(code)}`);
      await loadAiPrompts();
      if (aiPromptHistoryState.code === code) {
        await loadAiPromptHistory(code).catch(() => {});
      }
    } catch (err) {
      showFlash(err.message || `Failed to activate version ${version} for ${getPromptLabel(code)}`, 'error');
    } finally {
      if (triggerBtn) {
        triggerBtn.disabled = false;
        if (triggerBtn.dataset.originalText) {
          triggerBtn.textContent = triggerBtn.dataset.originalText;
          delete triggerBtn.dataset.originalText;
        }
      }
    }
  }

  function attachListeners() {
    resolveDom();
    if (aiPromptsContainer) {
      aiPromptsContainer.addEventListener('click', handleAiPromptContainerClick);
    }
    if (refreshPromptsBtn) {
      refreshPromptsBtn.addEventListener('click', () => {
        loadAiPrompts().catch(() => {});
      });
    }
    if (aiPromptHistoryList) {
      aiPromptHistoryList.addEventListener('click', handleAiPromptHistoryClick);
    }
    if (aiPromptHistoryDlg) {
      aiPromptHistoryDlg.addEventListener('close', () => {
        aiPromptHistoryState.code = null;
        aiPromptHistoryState.items = [];
        aiPromptHistoryState.activeVersion = null;
        if (aiPromptHistoryStatus) aiPromptHistoryStatus.textContent = '';
        if (aiPromptHistoryList) aiPromptHistoryList.innerHTML = '';
      });
    }
  }

  export default {
    configurePromptsState,
    attachListeners,
    init: () => { resolveDom(); loadAiPrompts(); },
    loadAiPrompts,
    renderAiPrompts,
    saveAiPrompt,
    destroyEditors: destroyAiPromptEditors,
    getPromptLabel,
    getEditors: () => aiPromptEditors,
  };
